import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '@/utils/store';
import { percentage } from '@/utils/format';
import { useCurrency } from '@/hooks/useCurrency';
import { Button } from '@/components/ui';
import PageHeader from '@/components/shared/PageHeader';
import ViewToggle from '@/components/ui/ViewToggle';
import AddGoalModal from '@/components/shared/AddGoalModal';
import AddFundsModal from '@/components/shared/AddFundsModal';
import { Plus, Check, Pencil, Trash2, Copy } from 'lucide-react';

export default function Goals() {
  const { t } = useTranslation();
  const { format: formatCurrency } = useCurrency();
  const goals = useStore((s) => s.goals);
  const removeGoal = useStore((s) => s.removeGoal);
  const duplicateGoal = useStore((s) => s.duplicateGoal);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editGoal, setEditGoal] = useState(null);
  const [fundGoal, setFundGoal] = useState(null);
  const [viewMode, setViewMode] = useState('list');

  const GoalCard = ({ g }) => {
    const pct = percentage(g.saved, g.target);
    const completed = g.saved >= g.target;

    return (
      <div className="card">
        <div className="flex items-center gap-3 mb-3">
          <span className="text-2xl">{g.icon}</span>
          <div className="flex-1">
            <div className="flex justify-between items-center">
              <span className="font-semibold text-gray-900 dark:text-white">
                {g.name}
              </span>
              {completed && viewMode === 'list' && (
                <span className="badge-income flex items-center gap-1">
                  <Check size={12} />
                  {t('goals.completed')}
                </span>
              )}
            </div>
            <div className="text-sm text-gray-500">
              {formatCurrency(g.saved)} {t('goals.saved')} · {formatCurrency(g.target)} {t('goals.target')}
            </div>
          </div>
        </div>

        <div className="h-3 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden mb-3">
          <div
            className="h-full rounded-full transition-all duration-500 bg-gradient-to-r from-brand-500 to-accent-500"
            style={{ width: `${pct}%` }}
          />
        </div>

        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-brand-600">{pct}%</span>
            <button
              onClick={() => duplicateGoal(g.id)}
              className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <Copy size={14} className="text-gray-400" />
            </button>
            <button
              onClick={() => { setEditGoal(g); setShowAddModal(true); }}
              className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <Pencil size={14} className="text-gray-400" />
            </button>
            <button
              onClick={() => removeGoal(g.id)}
              className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <Trash2 size={14} className="text-gray-400" />
            </button>
          </div>
          {!completed && (
            <button
              onClick={() => setFundGoal(g)}
              className="btn-secondary text-xs py-1.5 px-3"
            >
              + {t('goals.addFunds')}
            </button>
          )}
        </div>
      </div>
    );
  };

  const activeGoals = goals.filter((g) => g.saved < g.target);
  const completedGoals = goals.filter((g) => g.saved >= g.target);

  return (
    <div className="space-y-4 animate-fade-in">
      <PageHeader
        title={t('goals.title')}
        hint={t('common.hints.goals')}
        action={
          <div className="flex items-center gap-2">
            <ViewToggle view={viewMode} onChange={setViewMode} />
            <Button size="sm" icon={Plus} onClick={() => setShowAddModal(true)}>
              {t('goals.addNew')}
            </Button>
          </div>
        }
      />

      {goals.length === 0 ? (
        <div className="card text-center py-12">
          <div className="text-4xl mb-3">🎯</div>
          <p className="text-sm text-gray-500 dark:text-gray-400 max-w-xs mx-auto">
            {t('goals.noGoals')}
          </p>
        </div>
      ) : viewMode === 'list' ? (
        <div className="space-y-3">
          {goals.map((g) => (
            <GoalCard key={g.id} g={g} />
          ))}
        </div>
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0">
          {[
            { key: 'active', label: t('goals.active') || t('projects.statusActive'), items: activeGoals, colorClass: 'bg-success-100 text-success-700 dark:bg-success-500/10 dark:text-success-400' },
            { key: 'completed', label: t('goals.completed'), items: completedGoals, colorClass: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300' },
          ].map((col) => (
            <div key={col.key} className="flex-shrink-0 w-72 md:flex-1 md:min-w-0">
              <div className="flex items-center gap-2 mb-3 px-1">
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md ${col.colorClass}`}>
                  {col.label}
                </span>
                <span className="text-xs text-gray-400 font-medium">{col.items.length}</span>
              </div>
              <div className="space-y-2 min-h-[100px] bg-gray-50 dark:bg-gray-900/50 rounded-xl p-2">
                {col.items.length === 0 ? (
                  <div className="text-center py-8 text-xs text-gray-400 dark:text-gray-500">
                    {t('common.columnEmpty')}
                  </div>
                ) : (
                  col.items.map((g) => <GoalCard key={g.id} g={g} />)
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <AddGoalModal
        open={showAddModal}
        onClose={() => { setShowAddModal(false); setEditGoal(null); }}
        goal={editGoal}
      />
      <AddFundsModal open={!!fundGoal} onClose={() => setFundGoal(null)} goal={fundGoal} />
    </div>
  );
}
